import { getApp, getApps, initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import {
  getFirestore,
  connectFirestoreEmulator,
  addDoc,
  collection,
  serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import {
  Analytics,
  getAnalytics,
  isSupported,
  logEvent as logFirebaseEvent,
  setUserId as firebaseSetUserId,
  setUserProperties as firebaseSetUserProperties,
} from 'firebase/analytics';

// Firebase configuration for BevPass project (same as v1)
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? '',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID ?? '',
};

// Initialize Firebase
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

const ensureSessionId = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }
  const storageKey = 'bp-session-id';
  let sessionId = window.sessionStorage.getItem(storageKey);
  if (!sessionId) {
    const uuid = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : Math.random().toString(36).slice(2);
    sessionId = uuid;
    window.sessionStorage.setItem(storageKey, sessionId);
  }
  return sessionId;
};

ensureSessionId();

// Analytics support (web-only). Memoize so we only attempt once per session.
let analyticsPromise: Promise<Analytics | null> | null = null;

const bootstrapAnalytics = () => {
  if (typeof window === 'undefined') {
    return Promise.resolve(null);
  }

  if (!firebaseConfig.measurementId) {
    console.warn('Firebase Analytics disabled: missing VITE_FIREBASE_MEASUREMENT_ID.');
    return Promise.resolve(null);
  }

  if (!analyticsPromise) {
    analyticsPromise = isSupported()
      .then((supported) => {
        if (!supported) {
          console.warn('Firebase Analytics not supported in this environment.');
          return null;
        }
        return getAnalytics(app);
      })
      .catch((error) => {
        console.warn('Unable to initialise Firebase Analytics:', error);
        return null;
      });
  }

  return analyticsPromise;
};

export const getAnalyticsInstance = () => bootstrapAnalytics();

const sanitizeParams = (params?: Record<string, unknown>) => {
  if (!params) {
    return undefined;
  }

  return Object.entries(params).reduce<Record<string, unknown>>((acc, [key, value]) => {
    if (value === undefined) {
      return acc;
    }

    if (value instanceof Date) {
      acc[key] = value.toISOString();
      return acc;
    }

    if (Array.isArray(value)) {
      acc[key] = value.map((item) => (item instanceof Date ? item.toISOString() : item));
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {});
};

export const logAnalyticsEvent = async (
  eventName: string,
  params?: Record<string, unknown>,
) => {
  const normalizedParams = sanitizeParams(params);

  try {
    const analytics = await bootstrapAnalytics();
    if (analytics) {
      logFirebaseEvent(analytics, eventName, normalizedParams);
    }
  } catch (error) {
    console.warn('Failed to send analytics event to GA4', error);
  }

  const currentUser = auth.currentUser;
  if (!currentUser) {
    return;
  }

  try {
    await addDoc(collection(db, 'analytics_events'), {
      event: eventName,
      source: 'admin',
      params: normalizedParams,
      createdAt: serverTimestamp(),
      userId: currentUser.uid,
      sessionId: window.sessionStorage.getItem('bp-session-id') ?? null,
      platform: 'web',
      context: {
        path: window.location.pathname,
        search: window.location.search,
      },
    });
  } catch (error) {
    console.warn('Failed to record analytics event in Firestore', error);
  }
};

export const setAnalyticsIdentity = async (userId: string | null) => {
  try {
    const analytics = await bootstrapAnalytics();
    if (!analytics) {
      return;
    }

    firebaseSetUserId(analytics, userId ?? undefined);
  } catch (error) {
    console.warn('Failed to set analytics user id', error);
  }
};

export const setAnalyticsUserProperties = async (
  properties: Record<string, string | number | boolean | null | undefined>,
) => {
  const sanitized = Object.entries(properties).reduce<Record<string, string>>((acc, [key, value]) => {
    if (value === undefined || value === null) {
      return acc;
    }
    if (typeof value === 'string') {
      acc[key] = value;
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      acc[key] = String(value);
    } else {
      acc[key] = JSON.stringify(value);
    }
    return acc;
  }, {});

  if (Object.keys(sanitized).length === 0) {
    return;
  }

  try {
    const analytics = await bootstrapAnalytics();
    if (!analytics) {
      return;
    }

    await firebaseSetUserProperties(analytics, sanitized);
  } catch (error) {
    console.warn('Failed to set analytics user properties', error);
  }
};

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
// Explicitly select region to match deployed functions (avoids region mismatch/CORS issues)
export const functions = getFunctions(app, 'us-central1');
export const storage = getStorage(app);

// Connect to Firebase emulators in development if needed
// Set to true for local development to avoid CORS issues
// Set to false to use cloud functions (works in production, CORS issues in localhost)
const USE_EMULATORS = false;

// Only use emulators if explicitly enabled and on localhost
const shouldUseEmulators = USE_EMULATORS && window.location.hostname === 'localhost';

if (shouldUseEmulators) {
  console.log('🔍 Development mode with emulators enabled...');
  
  try {
    connectFunctionsEmulator(functions, 'localhost', 5001);
    console.log('✅ Connected to Firebase Functions emulator at localhost:5001');
  } catch (error) {
    console.log('⚠️ Functions emulator connection failed:', error);
  }

  try {
    connectFirestoreEmulator(db, 'localhost', 8080);
    console.log('✅ Connected to Firebase Firestore emulator at localhost:8080');
  } catch (error) {
    console.log('⚠️ Firestore emulator connection failed:', error);
  }

  try {
    connectStorageEmulator(storage, 'localhost', 9199);
    console.log('✅ Connected to Firebase Storage emulator at localhost:9199');
  } catch (error) {
    console.log('⚠️ Storage emulator connection failed:', error);
  }
} else {
  console.log('🌐 Using cloud Firebase services (Functions & Firestore)');
  console.log('📍 Functions region: us-central1');
}

console.log('🚀 Firebase initialized for BevPass Admin v2');

export default app;
